import { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent } from "./ui/card";
import { PriorityBadge } from "./Badges";
import { STATUS_ORDER, STATUS_LABEL, relTime } from "../lib/labels";
import { Phone, Robot, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { openTicketFromEvent } from "../lib/openTicket";

// Use the reliable `is_ai` flag set by the backend when the sender matches one of
// the "Flux IA" sources configured in Paramètres → Boîte mail. Falls back to a
// subject-based heuristic only if the flag is missing (legacy tickets pre-toggle).
const isCall = (t) =>
  t?.is_ai === true ||
  (typeof t?.subject === "string" && /^\s*(nouvel\s+)?appel\b/i.test(t.subject));

export default function CallsBoard() {
  const nav = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const dragId = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      // Fetch a generous slice; filtering client-side keeps parity with Kanban view.
      const r = await api.get("/tickets", { params: { per_page: 200 } });
      const list = Array.isArray(r.data) ? r.data : (r.data.items || r.data.data || []);
      setTickets(list.filter(isCall));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const cols = STATUS_ORDER.map((s) => ({
    key: s, label: STATUS_LABEL[s],
    tickets: tickets.filter((t) => t.status === s),
  }));

  const onDrop = async (status) => {
    const id = dragId.current; dragId.current = null;
    if (!id) return;
    try {
      await api.patch(`/tickets/${id}`, { status });
      toast.success(`Déplacé → ${STATUS_LABEL[status]}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  // Extract phone number from subject if present (target format: "Appel : Nom · +33 ...")
  const extractPhone = (subject) => {
    if (!subject) return null;
    const m = subject.match(/[·-]\s*([+0-9 .\-()]{6,})\s*$/);
    return m ? m[1].trim() : null;
  };
  // Human-friendly title stripped of the "Appel :" / "Nouvel appel de" prefix
  const stripPrefix = (subject) =>
    (subject || "").replace(/^\s*(nouvel\s+)?appel\s*(de\s+|:\s*|·\s*)?/i, "").trim() || subject;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground flex-1">
          Tickets marqués <span className="mono">Flux IA</span> — issus des sources configurées dans <b>Paramètres → Boîte mail → Flux IA</b>. Le badge est attribué automatiquement à la création par le backend.
        </p>
        <span className="text-xs px-2 py-1 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 font-medium flex items-center gap-1">
          <Robot size={12}/> {tickets.length} appel{tickets.length > 1 ? "s" : ""}
        </span>
      </div>

      <div className="flex gap-4 overflow-x-auto pb-4">
        {cols.map((col) => (
          <div key={col.key}
            className="kanban-col shrink-0 rounded-lg bg-muted/30 border"
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDrop(col.key)}
            data-testid={`calls-col-${col.key}`}>
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="flex items-center gap-2">
                <span className={`status-dot status-${col.key}`}/>
                <div className="font-display font-semibold text-sm">{col.label}</div>
              </div>
              <span className="text-xs mono text-muted-foreground">{col.tickets.length}</span>
            </div>
            <div className="p-3 space-y-3 min-h-[240px]">
              {loading && col.tickets.length === 0 && (
                <div className="text-xs text-muted-foreground text-center py-6">Chargement…</div>
              )}
              {!loading && col.tickets.length === 0 && (
                <div className="text-xs text-muted-foreground text-center py-6">Aucun appel</div>
              )}
              {col.tickets.map((t) => {
                const phone = extractPhone(t.subject);
                return (
                  <Card key={t.id}
                    draggable
                    onDragStart={() => (dragId.current = t.id)}
                    onClick={(e) => openTicketFromEvent(e, `/tickets/${t.id}`, nav)}
                    onAuxClick={(e) => openTicketFromEvent(e, `/tickets/${t.id}`, nav)}
                    className="cursor-pointer card-hover border-l-4 border-l-blue-500"
                    data-testid={`calls-card-${t.number}`}>
                    <CardContent className="p-3 space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <Link
                          to={`/tickets/${t.id}`}
                          data-testid={`calls-card-link-${t.number}`}
                          onClick={(e) => e.stopPropagation()}
                          className="mono text-muted-foreground hover:underline"
                        >
                          #{t.number}
                        </Link>
                        <PriorityBadge value={t.priority}/>
                      </div>
                      <div className="font-medium text-sm leading-snug line-clamp-2 flex items-start gap-1">
                        <Robot size={14} className="mt-0.5 shrink-0 text-blue-500"/>
                        <span>{stripPrefix(t.subject)}</span>
                      </div>
                      {phone && (
                        <a href={`tel:${phone.replace(/\s+/g, "")}`}
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-1.5 text-xs mono text-blue-600 hover:underline dark:text-blue-400"
                          data-testid={`calls-phone-${t.number}`}>
                          <Phone size={12}/> {phone}
                        </a>
                      )}
                      <div className="text-xs text-muted-foreground truncate">
                        {t.client_name} · {t.assignee_name || "non assigné"}
                      </div>
                      <div className="text-[0.65rem] text-muted-foreground flex items-center gap-1">
                        <Clock size={10}/> {relTime(t.created_at)}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
